// Required jQuery type for TypeScript
type JQueryStatic = {
  (selector: string | Element): {
    on: (event: string, callback: (event: any, ...args: any[]) => void) => void;
  };
  (document: Document): {
    on: (event: string, callback: (event: any, ...args: any[]) => void) => void;
  };
};

// Core interfaces
interface FormData {
  contacts: {
    [key: string]: {
      tv_audio: { email: string; tel: string; time?: string };
      e_scooters: { email: string; tel: string };
      e_bikes: { email: string; tel: string };
      officer?: string;
      submit_email: string;
    };
  };
  officer: { [key: string]: string };
}

interface Window {
  gform: {
    addAction: (hook: string, callback: (...args: any[]) => void) => void;
  };
  app_vars: { restUrl: string };
  jQuery: JQueryStatic;
}

// DOM Selectors
const SELECTORS = {
  COUNTRY_WRAPPER: '.gfield.js-country-wrapper',
  CONTACTS_CARDS: '.js-contacts-cards .contacts-cards',
  OFFICER_FIELD: '.js-data-protection-officer .data-protection-officer',
  FORM_FIELDS: '.gfield:not(.js-country-wrapper)',
  SUBMIT_BUTTON: '[id^="gform_submit_button_"]',
  COUNTRY_EMAIL: '.js-country-email input[type="email"]',
  PRODUCT_TYPE: '.gfield.product-type',
  POPULATE_MODELS: '.gfield.populate-models',
} as const;

/**
 * Main form handler class
 * Note: jQuery is required for proper form handling after validation
 */
class FormHandler {
  private static updateFormFields(
    form: HTMLFormElement,
    isEnabled: boolean,
  ): void {
    form
      .querySelectorAll<HTMLElement>(SELECTORS.FORM_FIELDS)
      .forEach((field) => {
        field
          .querySelectorAll<HTMLElement>('input, select, textarea, button')
          .forEach((input) => {
            input.toggleAttribute('disabled', !isEnabled);
            field.style.opacity = isEnabled ? '1' : '0.5';
          });
      });

    const submitButton = form.querySelector<HTMLButtonElement>(
      SELECTORS.SUBMIT_BUTTON,
    );
    if (submitButton) {
      submitButton.disabled = !isEnabled;
      submitButton.style.opacity = isEnabled ? '1' : '0.5';
      submitButton.style.cursor = isEnabled ? 'pointer' : 'not-allowed';
    }
  }

  private static updateContactCards(
    form: HTMLFormElement,
    contacts: any,
    contactsCardsField: HTMLDivElement,
  ): void {
    const template = form.dataset.contactBoxesTemplate || '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = template;

    tempDiv.querySelectorAll('[data-show-if]').forEach((element) => {
      const path = element.getAttribute('data-show-if')?.split('.') || [];
      let value = contacts;
      for (const key of path) {
        value = value?.[key];
        if (!value) break;
      }
      element.classList.toggle('hidden', !value || String(value).trim() === '');
    });

    contactsCardsField.innerHTML = tempDiv.innerHTML.replace(
      /%contacts\.([^%]+)%/g,
      (_, path) => {
        const keys = path.split('.');
        let value = contacts;
        for (const key of keys) {
          value = value?.[key];
          if (!value) return '';
        }
        return String(value);
      },
    );
    contactsCardsField.style.display = 'block';
  }

  private static handleCountrySelection(
    form: HTMLFormElement,
    select: HTMLSelectElement,
  ): void {
    const selectedValue = select.value;
    if (!form) return;

    // Update form fields state
    this.updateFormFields(form, Boolean(selectedValue));

    // Get and validate form data
    const formData: FormData = JSON.parse(form.dataset.formData || '{}');
    if (!formData?.contacts) return;

    // Update contact cards if they exist
    const contactsCardsField = form.querySelector<HTMLDivElement>(
      SELECTORS.CONTACTS_CARDS,
    );
    if (
      contactsCardsField &&
      selectedValue &&
      formData.contacts[selectedValue]
    ) {
      this.updateContactCards(
        form,
        formData.contacts[selectedValue],
        contactsCardsField,
      );
    }

    // Update email field if it exists
    if (selectedValue && formData.contacts[selectedValue]?.submit_email) {
      const emailInput = form.querySelector<HTMLInputElement>(
        SELECTORS.COUNTRY_EMAIL,
      );
      if (emailInput) {
        emailInput.value = formData.contacts[selectedValue].submit_email;
      }
    }

    // Update officer field if it exists
    const officerField = form.querySelector<HTMLDivElement>(
      SELECTORS.OFFICER_FIELD,
    );
    if (officerField && selectedValue) {
      const officerText = formData.officer?.[selectedValue];
      if (officerText) {
        const template = form.dataset.officerTextTemplate;
        officerField.innerHTML = template
          ? template.replace('{{text}}', officerText.replace(/\\'/g, "'"))
          : '';
        officerField.style.display = 'block';
      } else {
        officerField.style.display = 'none';
      }
    }
  }

  /**
   * Handles product type selection and updates model choices
   */
  private static async handleProductTypeChange(
    elem: HTMLElement,
    previousModelValue?: string,
  ): Promise<void> {
    const formHasProductTypeField = elem.closest(SELECTORS.PRODUCT_TYPE);
    if (!formHasProductTypeField) return;

    const populateModelsField = elem
      .closest('.gform_body')
      ?.querySelector(SELECTORS.POPULATE_MODELS);
    const populateModelsSelect = populateModelsField?.querySelector('select');
    if (!populateModelsField || !populateModelsSelect) return;

    const productType = (elem as HTMLSelectElement).value;
    populateModelsField.classList.add('is-loading');

    try {
      const response = await fetch(
        `${window.app_vars.restUrl}sharp/support-products-model-choices?category=${productType}`,
        { method: 'GET' },
      );
      if (!response.ok) throw new Error('Network response was not ok');

      // Store current selection if none provided
      const valueToRestore = previousModelValue || populateModelsSelect.value;

      // Update options
      populateModelsSelect.innerHTML = await response.json();

      // Restore previous selection if it exists in new options
      if (valueToRestore) {
        const option = Array.from(populateModelsSelect.options).find(
          (opt) => opt.value === valueToRestore,
        );
        if (option) {
          populateModelsSelect.value = valueToRestore;
          // Trigger change event to ensure any listeners are notified
          populateModelsSelect.dispatchEvent(new Event('change'));
        }
      }
    } catch (error) {
      console.error('Error fetching model choices:', error);
    } finally {
      populateModelsField.classList.remove('is-loading');
    }
  }

  private static async initializeProductType(
    form: HTMLFormElement,
  ): Promise<void> {
    const productTypeSelect = form.querySelector<HTMLSelectElement>(
      `${SELECTORS.PRODUCT_TYPE} select`,
    );
    const modelsSelect = form.querySelector<HTMLSelectElement>(
      `${SELECTORS.POPULATE_MODELS} select`,
    );

    if (!productTypeSelect || !modelsSelect) return;

    // Store the current model selection
    const previousModelValue = modelsSelect.value;

    // Always reinitialize if there's a product type value
    if (productTypeSelect.value) {
      await this.handleProductTypeChange(productTypeSelect, previousModelValue);
    }
  }

  private static hasCountryField(form: HTMLFormElement): boolean {
    return Boolean(form.querySelector(SELECTORS.COUNTRY_WRAPPER));
  }

  private static initializeForm(form: HTMLFormElement): void {
    // Only proceed if form has country field
    if (!this.hasCountryField(form)) return;

    const select = form.querySelector<HTMLSelectElement>(
      `${SELECTORS.COUNTRY_WRAPPER} select`,
    );
    if (!select) return;

    // The default country is preselected server-side from the site locale (forms.php).
    // Handle initial selection
    this.handleCountrySelection(form, select);

    // Add change listener if not already added
    if (!select.hasAttribute('data-has-listener')) {
      select.addEventListener('change', () =>
        this.handleCountrySelection(form, select),
      );
      select.setAttribute('data-has-listener', 'true');
    }

    // Initialize product type if it exists
    this.initializeProductType(form);
  }

  public static init(): void {
    if (typeof window.gform === 'undefined') return;

    // Initialize all forms on page load
    document
      .querySelectorAll<HTMLFormElement>('form[id^="gform_"]')
      .forEach((form) => {
        if (this.hasCountryField(form)) {
          this.initializeForm(form);
        }
      });

    // Handle form render after validation (requires jQuery)
    window.jQuery(document).on('gform_post_render', (_, formId) => {
      const form = document.getElementById(
        `gform_${formId}`,
      ) as HTMLFormElement;
      if (form && this.hasCountryField(form)) {
        // Ensure we wait for the DOM to be updated
        setTimeout(() => {
          this.initializeForm(form);
        }, 100);
      }
    });

    // Handle input changes for country selection and product type
    window.gform.addAction('gform_input_change', (elem: HTMLElement) => {
      const form = elem.closest('form');
      if (!form || !this.hasCountryField(form)) return;

      // Handle country selection
      if (elem.closest(SELECTORS.COUNTRY_WRAPPER)) {
        if (form) this.handleCountrySelection(form, elem as HTMLSelectElement);
      }

      // Handle product type selection
      if (elem.closest(SELECTORS.PRODUCT_TYPE)) {
        this.handleProductTypeChange(elem);
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => FormHandler.init());
